import { useState } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    ListSubheader,
    CircularProgress,
    Box,
    Chip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useLanguage } from '../../../../context/LanguageContext';
import CreateGroupDialog from './CreateGroupDialog';

interface GroupSelectorProps {
    selectedGroupId: number | null;
    newGroupName?: string;
    availableGroups: Array<{ id: number; description: string }>;
    loading?: boolean;
    fullWidth?: boolean;
    onGroupIdChange: (groupId: number | null, groupName?: string) => void;
    onCreateGroup: (groupName: string) => void | Promise<void>;
    onRefetchGroups?: () => void | Promise<void>;
}

export function GroupSelector({
    selectedGroupId,
    newGroupName,
    availableGroups,
    loading = false,
    fullWidth = false,
    onGroupIdChange,
    onCreateGroup,
    onRefetchGroups,
}: GroupSelectorProps) {
    const { t } = useLanguage();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectOpen, setSelectOpen] = useState(false);

    const handleChange = (value: string | number) => {
        setSelectOpen(false);
        if (value === 'CREATE_NEW') {
            setDialogOpen(true);
        } else if (value) {
            const id = Number(value);
            const group = availableGroups.find(g => g.id === id);
            onGroupIdChange(id, group?.description);
        } else {
            onGroupIdChange(null);
        }
    };

    const handleCreate = async (groupName: string) => {
        setSelectOpen(false);
        await onCreateGroup(groupName);
        setDialogOpen(false);
        await onRefetchGroups?.();
    };

    return (
        <>
            <FormControl size="small" sx={{ minWidth: fullWidth ? undefined : '250px' }} fullWidth={fullWidth}>
                <InputLabel>{t('pages.clients.queryBuilder.groupSelector.label')}</InputLabel>
                <Select
                    value={selectedGroupId || ''}
                    label={t('pages.clients.queryBuilder.groupSelector.label')}
                    open={selectOpen}
                    onOpen={() => setSelectOpen(true)}
                    onClose={() => setSelectOpen(false)}
                    onChange={(e) => handleChange(e.target.value as string | number)}
                    disabled={loading}
                    startAdornment={loading ? (
                        <CircularProgress size={16} thickness={5} sx={{ ml: 1, mr: 0.5, flexShrink: 0 }} />
                    ) : null}
                    MenuProps={{
                        PaperProps: {
                            sx: { maxHeight: 340 },
                        },
                    }}
                >
                    <MenuItem value=""><em>{t('pages.clients.queryBuilder.groupSelector.none')}</em></MenuItem>
                    {selectedGroupId === -1 && newGroupName && (
                        <MenuItem value={-1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <em>{newGroupName}</em>
                                <Chip size="small" label={t('pages.clients.queryBuilder.groupSelector.new')} color="primary" />
                            </Box>
                        </MenuItem>
                    )}
                    {availableGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                            {group.description}
                        </MenuItem>
                    ))}
                    <ListSubheader
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            zIndex: 2,
                            p: 0,
                            lineHeight: 'inherit',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Button
                            fullWidth
                            startIcon={<AddIcon />}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={() => {
                                setSelectOpen(false);
                                setDialogOpen(true);
                            }}
                            sx={{ textTransform: 'none', justifyContent: 'flex-start', color: '#556ee6', fontWeight: 600 }}
                        >
                            {t('pages.clients.queryBuilder.createGroupDialog.title')}
                        </Button>
                    </ListSubheader>
                </Select>
            </FormControl>

            <CreateGroupDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setSelectOpen(false);
                }}
                onConfirm={handleCreate}
            />
        </>
    );
}

export default GroupSelector;
